<template>
  <transition name="modal" appear>
    <div class="modal-overlay" @click.self="closeModal">
      <div class="modal-content membership-modal-content">
        <div class="logintop-head membership-head">
          <p>Choose your membership</p>
          <img src="/images/logo.png" alt="SYNAPSE">
        </div>

        <p class="membership-subtitle">Select a plan to complete your signup</p>

        <div class="membership-plans">
          <button
            v-for="plan in plans"
            :key="plan.id"
            type="button"
            class="membership-pill"
            :class="[plan.toneClass, { selected: selectedPlan === plan.id }]"
            @click="selectedPlan = plan.id"
          >
            <div class="membership-pill-body">
              <h4>
                <img :src="plan.image" :alt="plan.title" />
                {{ plan.title }}
              </h4>
              <div class="membership-price-row">
                <span class="price-label">Price</span>
                <span class="price-value">{{ plan.price }}</span>
              </div>
            </div>
            <span v-if="selectedPlan === plan.id" class="selected-check" aria-hidden="true">
              <i class="fa fa-check"></i>
            </span>
            <div class="cardbottom-shadow">
              <img src="/images/cardshadow.png" alt="">
            </div>
          </button>
        </div>

        <div class="form-message" v-if="message">
          <div :class="messageType === 'success' ? 'inline-success' : 'inline-error'">
            {{ message }}
          </div>
        </div>

        <div class="membership-actions">
          <button type="button" class="back-btn" :disabled="isLoading" @click="goBack">
            Back
          </button>
          <button
            type="button"
            class="submit-btn"
            :disabled="!selectedPlan || isLoading"
            @click="continueToPayment"
          >
            <span>Continue</span>
            <div class="cardbottom-shadow">
              <img src="/images/cardshadow.png" alt="">
            </div>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { lockBodyScrollInline, releaseBodyScrollLock } from '@/views/lib/bodyScrollLock';
import { MEMBERSHIP_PLANS } from '@/views/lib/pendingSignup';

export default {
  name: 'MembershipModal',
  props: {
    signupData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedPlan: null,
      message: '',
      messageType: '',
      isLoading: false,
      plans: MEMBERSHIP_PLANS,
    };
  },
  mounted() {
    lockBodyScrollInline();
  },
  beforeDestroy() {
    releaseBodyScrollLock();
  },
  methods: {
    goBack() {
      releaseBodyScrollLock();
      this.$emit('back');
    },
    closeModal() {
      if (this.isLoading) return;
      releaseBodyScrollLock();
      this.$emit('close');
    },
    continueToPayment() {
      if (!this.selectedPlan) {
        this.message = 'Please select a membership plan.';
        this.messageType = 'error';
        return;
      }
      releaseBodyScrollLock();
      this.$emit('selected', this.selectedPlan);
    },
  },
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.4s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.4s ease;
}

.modal-enter-from .modal-content {
  transform: translateY(-40px);
}

.modal-leave-to .modal-content {
  transform: translateY(-40px);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ffffffc7;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
  padding: 20px 12px;
  box-sizing: border-box;
}

.modal-content {
  background: white;
  padding: 40px 40px 0;
  border-radius: 50px;
  position: relative;
  width: 100%;
  max-width: 380px;
  max-height: 92vh;
  overflow-y: auto;
  will-change: transform;
  transform: translateY(0);
  box-shadow: 0 0 23.34px #000000BF;
}

.membership-head {
  padding-bottom: 12px !important;
  text-align: center;
}

.membership-subtitle {
  text-align: center;
  color: #6D6E71;
  font-size: 13px;
  font-weight: 300;
  margin: 0 0 22px;
}

.membership-plans {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin: 0 auto 18px;
  width: 100%;
  max-width: 260px;
}

.membership-pill {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  border: none;
  border-radius: 16px;
  padding: 8px 10px 6px;
  position: relative;
  cursor: pointer;
  text-align: center;
  box-sizing: border-box;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  font: inherit;
  outline: none;
}

.membership-pill:focus {
  outline: none;
}

.membership-pill:hover {
  transform: translateY(-2px);
}

.membership-pill.selected {
  box-shadow: 0 0 0 3px #231F20;
  transform: translateY(-2px);
}

.tone-questions {
  background: #1a90ff;
  border: 1px solid #0276ba;
}

.tone-notes {
  background: #fbad18;
  border: 1px solid #e69500;
}

.tone-mocks {
  background: #ed2724;
  border: 1px solid #c41a1f;
}

.tone-all {
  background: #20b14b;
  border: 1px solid #178a39;
}

.membership-pill-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.membership-pill h4 {
  margin: 0;
  font-size: 18px;
  color: #fff;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  font-family: Helvetica, sans-serif;
  font-weight: 700;
  line-height: 1.2;
}

.membership-pill h4 img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.membership-price-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 2px 4px 0;
}

.price-label {
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
  font-weight: 500;
}

.price-value {
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.selected-check {
  position: absolute;
  top: 8px;
  right: 10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #231F20;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  z-index: 2;
}

.membership-pill .cardbottom-shadow img {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -18px;
  width: 220px;
  height: auto;
  z-index: -1;
  pointer-events: none;
  opacity: 0.75;
}

.membership-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 6px 0 28px;
}

.back-btn {
  background: transparent;
  color: #8698A2;
  border: 2px solid #8698A2;
  border-radius: 35px;
  cursor: pointer;
  width: 100px;
  height: 29.60px;
  font-weight: bold;
  font-size: 14px;
  transition: all 0.6s ease;
}

.back-btn:hover:not(:disabled) {
  background: #8698a278;
  color: #fff;
}

.back-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-btn {
  background-color: #9DED6C;
  color: #231F20;
  padding: 5px 0;
  border: 1px solid #00a14b75;
  border-radius: 35px;
  cursor: pointer;
  width: 111.84px;
  transition: all 0.6s ease;
  font-weight: bold;
  font-size: 14px;
  height: 29.60px;
  position: relative;
}

.submit-btn:hover:not(:disabled) {
  background: #20B14B;
  color: #fff;
}

.submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.submit-btn .cardbottom-shadow img {
  left: 50%;
  transform: translate(-50%);
  width: 200px;
}

.form-message {
  margin-bottom: 8px;
}

.inline-error,
.inline-success {
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 12px;
  text-align: center;
}

.inline-error {
  background: #ffebee;
  color: #e53935;
}

.inline-success {
  background: #e8f5e9;
  color: #2e7d32;
}

@media (max-width: 480px) {
  .modal-content {
    padding: 28px 22px 0;
    border-radius: 36px;
  }
}
</style>
